/**
 * AiChatProvider — app-level context for AI chat state.
 *
 * This component wraps the entire app and provides:
 *   - Section context (what dashboard page the user is on)
 *   - Panel toggle (open/close the slide-over)
 *   - Thread state (current thread, session key for resets)
 *
 * It does NOT mount the assistant-ui runtime here — that lives in
 * AiChatRuntime, which wraps only the chat UI (AiChat component).
 * This separation means thread switching remounts only the chat area,
 * not the sidebar or the rest of the app.
 */
import {
  type ReactNode,
  createContext,
  useContext,
  useCallback,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

// ── Section Context ─────────────────────────────────────────────────────────

interface SectionContextValue {
  sectionContext: unknown;
  setSectionContext: (ctx: unknown) => void;
}

const SectionCtx = createContext<SectionContextValue>({
  sectionContext: null,
  setSectionContext: () => {},
});

export function useSectionContext() {
  return useContext(SectionCtx);
}

// ── Panel Toggle ────────────────────────────────────────────────────────────

interface PanelToggleValue {
  panelOpen: boolean;
  togglePanel: () => void;
  closePanel: () => void;
}

const PanelToggleCtx = createContext<PanelToggleValue>({
  panelOpen: false,
  togglePanel: () => {},
  closePanel: () => {},
});

export function useAiChatPanel() {
  return useContext(PanelToggleCtx);
}

// ── Thread State ─────────────────────────────────────────────────────────────

interface ThreadStateValue {
  currentThreadId: string | null;
  switchThread: (id: string | null) => void;
  /** Adopt a threadId coming FROM the URL (no navigation) — reconciles route → state. */
  openThreadFromRoute: (id: string | null) => void;
  /** Update threadId without resetting the chat session (used after backend creates a thread) */
  setCurrentThreadIdDirect: (id: string | null) => void;
  threadVersion: number;
  refreshThreads: () => void;
  chatSessionKey: number;
  /** Remount the current thread's runtime so it refetches from the server (used after revert). */
  reloadCurrentThread: () => void;
  /** Text to seed into the composer on the next runtime mount (Cursor-style revert). */
  pendingComposerText: string | null;
  setPendingComposerText: (text: string | null) => void;
}

const ThreadStateCtx = createContext<ThreadStateValue>({
  currentThreadId: null,
  switchThread: () => {},
  openThreadFromRoute: () => {},
  setCurrentThreadIdDirect: () => {},
  threadVersion: 0,
  refreshThreads: () => {},
  chatSessionKey: 0,
  reloadCurrentThread: () => {},
  pendingComposerText: null,
  setPendingComposerText: () => {},
});

export function useThreadState() {
  return useContext(ThreadStateCtx);
}

// ── Model Selection ──────────────────────────────────────────────────────────

/** How hard the model thinks (the API's `resolveEffort` keeps it to each model's levels). */
export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type EffortLevel = (typeof EFFORT_LEVELS)[number];
export const EFFORT_LABELS: Record<EffortLevel, string> = {
  low: 'Low', medium: 'Medium', high: 'High', xhigh: 'Extra high', max: 'Max',
};
const GEMINI_EFFORTS = ['low', 'medium', 'high'] as const;

/** `providerDefault`: the provider's own default, which a model runs at until its effort is changed. */
export const AI_MODELS = [
  { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', provider: 'google', efforts: GEMINI_EFFORTS, providerDefault: 'medium' },
  { id: 'gemini-3.1-pro-preview-customtools', label: 'Gemini 3.1 Pro', provider: 'google', efforts: GEMINI_EFFORTS, providerDefault: 'high' },
  { id: 'gpt-6-astra', label: 'GPT-6 Astra', provider: 'openai', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  { id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', provider: 'openai', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  { id: 'gpt-6.1-terra', label: 'GPT-6.1 Terra', provider: 'openai', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  { id: 'gpt-6-luna', label: 'GPT-6 Luna', provider: 'openai', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  { id: 'claude-fable-5-1', label: 'Fable 5.1', provider: 'anthropic', efforts: EFFORT_LEVELS, providerDefault: 'high' },
  { id: 'claude-opus-5-5', label: 'Opus 5.5', provider: 'anthropic', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  { id: 'claude-sonnet-5-5', label: 'Sonnet 5.5', provider: 'anthropic', efforts: EFFORT_LEVELS, providerDefault: 'high' },
  { id: 'claude-haiku-5-5', label: 'Haiku 5.5', provider: 'anthropic', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  // The same Claude models, run by Claude Code on the company Mac under the Claude subscription.
  { id: 'mac/claude-fable-5-1', label: 'Fable 5.1 (Mac)', provider: 'mac-claude', efforts: EFFORT_LEVELS, providerDefault: 'high' },
  { id: 'mac/claude-opus-5-5', label: 'Opus 5.5 (Mac)', provider: 'mac-claude', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
  { id: 'mac/claude-sonnet-5-5', label: 'Sonnet 5.5 (Mac)', provider: 'mac-claude', efforts: EFFORT_LEVELS, providerDefault: 'high' },
  { id: 'mac/claude-haiku-5-5', label: 'Haiku 5.5 (Mac)', provider: 'mac-claude', efforts: EFFORT_LEVELS, providerDefault: 'medium' },
] as const;

export type AiModelId = (typeof AI_MODELS)[number]['id'];


const DEFAULT_MODEL: AiModelId = 'gemini-3.8-flash';

interface ModelSelectionValue {
  selectedModel: AiModelId;
  setSelectedModel: (id: AiModelId) => void;
  /** The selected model's effort. */
  selectedEffort: EffortLevel;
  setSelectedEffort: (effort: EffortLevel) => void;
}

const ModelSelectionCtx = createContext<ModelSelectionValue>({
  selectedModel: DEFAULT_MODEL,
  setSelectedModel: () => {},
  selectedEffort: 'medium',
  setSelectedEffort: () => {},
});

export function useModelSelection() {
  return useContext(ModelSelectionCtx);
}

const MODEL_STORAGE_KEY = 'ai-chat-model';

function readPersistedModel(): AiModelId {
  try {
    const stored = localStorage.getItem(MODEL_STORAGE_KEY);
    if (stored && AI_MODELS.some((m) => m.id === stored)) return stored as AiModelId;
  } catch { /* noop */ }
  return DEFAULT_MODEL;
}

function writePersistedModel(id: AiModelId): void {
  try {
    localStorage.setItem(MODEL_STORAGE_KEY, id);
  } catch { /* noop */ }
}

// Effort is remembered per model: a level picked for Opus means nothing for Gemini.
const EFFORT_STORAGE_KEY = 'ai-chat-effort';

function readPersistedEfforts(): Partial<Record<AiModelId, EffortLevel>> {
  try {
    const parsed = JSON.parse(localStorage.getItem(EFFORT_STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePersistedEfforts(efforts: Partial<Record<AiModelId, EffortLevel>>): void {
  try {
    localStorage.setItem(EFFORT_STORAGE_KEY, JSON.stringify(efforts));
  } catch { /* noop */ }
}

/** `model`'s remembered effort, if it still has that level; else the provider's default. */
export function effortFor(model: AiModelId, efforts: Partial<Record<AiModelId, EffortLevel>>): EffortLevel {
  const entry = AI_MODELS.find((m) => m.id === model) ?? AI_MODELS[0];
  const levels: readonly string[] = entry.efforts;
  const stored = efforts[model];
  if (stored && levels.includes(stored)) return stored;
  return entry.providerDefault;
}

// ── Session Storage ─────────────────────────────────────────────────────────

const THREAD_STORAGE_KEY = 'ai-chat-thread-id';

function readPersistedThreadId(): string | null {
  try {
    return sessionStorage.getItem(THREAD_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersistedThreadId(id: string | null): void {
  try {
    if (id) {
      sessionStorage.setItem(THREAD_STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(THREAD_STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable (e.g. private browsing quota exceeded)
  }
}

/**
 * Initial thread id. The URL is the source of truth: a deep link like
 * `/ai-chat/<id>` wins so a hard refresh loads the right thread on the very
 * first render (no A→B double-fetch). Bare `/ai-chat` falls back to the last
 * thread persisted in sessionStorage.
 */
function readInitialThreadId(): string | null {
  try {
    const m = window.location.pathname.match(/^\/ai-chat\/([^/]+)\/?$/);
    if (m) return decodeURIComponent(m[1]);
  } catch {
    // window unavailable — fall through to sessionStorage
  }
  return readPersistedThreadId();
}

// ── Provider ────────────────────────────────────────────────────────────────

export function AiChatProvider({ children }: { children: ReactNode }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const togglePanel = useCallback(() => setPanelOpen((o) => !o), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const [sectionContext, setSectionContext] = useState<unknown>(null);

  const navigate = useNavigate();
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(() => readInitialThreadId());
  const [threadVersion, setThreadVersion] = useState(0);
  const [chatSessionKey, setChatSessionKey] = useState(0);
  const [pendingComposerText, setPendingComposerText] = useState<string | null>(null);

  const [selectedModel, setSelectedModelRaw] = useState<AiModelId>(() => readPersistedModel());
  const setSelectedModel = useCallback((id: AiModelId) => {
    setSelectedModelRaw(id);
    writePersistedModel(id);
  }, []);

  const [efforts, setEfforts] = useState(() => readPersistedEfforts());
  const selectedEffort = effortFor(selectedModel, efforts);
  const setSelectedEffort = useCallback((effort: EffortLevel) => {
    setEfforts((prev) => {
      const next = { ...prev, [selectedModel]: effort };
      writePersistedEfforts(next);
      return next;
    });
  }, [selectedModel]);

  const refreshThreads = useCallback(() => setThreadVersion((v) => v + 1), []);

  // Apply a thread selection to state (+ remount the runtime to load it).
  // Navigation is handled separately so this can be reused by the URL reconciler.
  const applyThread = useCallback((id: string | null) => {
    setCurrentThreadId(id);
    writePersistedThreadId(id);
    setChatSessionKey((k) => k + 1);
  }, []);

  const switchThread = useCallback((id: string | null) => {
    applyThread(id);
    navigate(id ? `/ai-chat/${id}` : '/ai-chat');
  }, [applyThread, navigate]);

  // Reconcile a thread id that arrived FROM the URL (deep link, browser
  // back/forward) into state — without navigating (that would loop).
  const openThreadFromRoute = useCallback((id: string | null) => {
    applyThread(id);
  }, [applyThread]);

  const setCurrentThreadIdDirect = useCallback((id: string | null) => {
    setCurrentThreadId(id);
    writePersistedThreadId(id);
  }, []);

  // Bumping the session key remounts AiChatRuntime, which re-fetches the thread
  // via threadApi.get — needed because useChat ignores post-mount message
  // changes, so a server-side truncation (revert) only surfaces on remount.
  const reloadCurrentThread = useCallback(() => {
    setChatSessionKey((k) => k + 1);
  }, []);

  return (
    <ModelSelectionCtx.Provider value={{ selectedModel, setSelectedModel, selectedEffort, setSelectedEffort }}>
      <SectionCtx.Provider value={{ sectionContext, setSectionContext }}>
        <PanelToggleCtx.Provider value={{ panelOpen, togglePanel, closePanel }}>
          <ThreadStateCtx.Provider
            value={{
              currentThreadId,
              switchThread,
              openThreadFromRoute,
              setCurrentThreadIdDirect,
              threadVersion,
              refreshThreads,
              chatSessionKey,
              reloadCurrentThread,
              pendingComposerText,
              setPendingComposerText,
            }}
          >
            {children}
          </ThreadStateCtx.Provider>
        </PanelToggleCtx.Provider>
      </SectionCtx.Provider>
    </ModelSelectionCtx.Provider>
  );
}
